/*
NOTE: The functionality used in this component is extracted from react-phone-input-2 plugin.
*/

import memoize from 'lodash/memoize';
import reduce from 'lodash/reduce';
import startsWith from 'lodash/startsWith';
import React, {
    ReactElement,
} from 'react';

import { IStyleableProps } from '../../../shared/types/IStyleableProps';
import { defaultProps } from '../PhoneNumber.config';
import CountryData from '../vendor/CountryData';

export type IPhoneNumberDisplayProps = IStyleableProps & {
    phoneNumber: string;
};

export function OzPhoneNumberDisplay (props: IPhoneNumberDisplayProps): ReactElement {
    const {
        phoneNumber = '',
        className = '',
    }: IPhoneNumberDisplayProps = props;

    const { onlyCountries, hiddenAreaCodes } = new CountryData(
        defaultProps.enableAreaCodes, defaultProps.enableTerritories, defaultProps.regions,
        defaultProps.onlyCountries, defaultProps.preferredCountries, defaultProps.excludeCountries, defaultProps.preserveOrder,
        defaultProps.masks, defaultProps.priority, defaultProps.areaCodes, defaultProps.localization,
        defaultProps.prefix, defaultProps.defaultMask, defaultProps.alwaysDefaultMask,
    );

    const inputNumber = phoneNumber.replace(/\D/g, '') || '';

    const guessSelectedCountry = memoize((inputNumber: any, country: any, onlyCountries: any, hiddenAreaCodes: any): any=> {
        // if enableAreaCodes == false, try to search in hidden area codes to detect area code correctly
        // then search and insert main country which has this area code
        // https://github.com/bl00mber/react-phone-input-2/issues/201
        if (defaultProps.enableAreaCodes === false) {
            let mainCode;

            for (const country of hiddenAreaCodes) {
                if (startsWith(inputNumber, country.dialCode)) {
                    mainCode = onlyCountries.find((o: any): any => {
                        return (country.iso2 === o.iso2) && o.mainCode;
                    });
                    break;
                }
            }

            if (mainCode) {
                return mainCode;
            }
        }

        const secondBestGuess = onlyCountries.find((o: any): any => {return o.iso2 === country;});

        if (inputNumber.trim() === '') return secondBestGuess;

        const bestGuess = onlyCountries.reduce((selectedCountry: any, country: any): any => {
            if (startsWith(inputNumber, country.dialCode)) {
                if (country.dialCode.length > selectedCountry.dialCode.length) {
                    return country;
                }

                if (country.dialCode.length === selectedCountry.dialCode.length && country.priority < selectedCountry.priority) {
                    return country;
                }
            }

            return selectedCountry;
        }, { dialCode: '',
            priority: 10001 }, guessSelectedCountry);

        if (!bestGuess.name) return secondBestGuess;
        return bestGuess;
    });

    let countryGuess;

    if (inputNumber.length > 1) {
        // Country detect by phone
        countryGuess = guessSelectedCountry(inputNumber.substring(0, 6), defaultProps.country, onlyCountries, hiddenAreaCodes) || 0;
    } else if (defaultProps.country) {
        // Default country
        countryGuess = onlyCountries.find((o: any): any => {return o.iso2 === defaultProps.country;}) || 0;
    } else {
        // Empty params
        countryGuess = 0;
    }

    const dialCode = (
        inputNumber.length < 2 &&
    countryGuess &&
    !startsWith(inputNumber, countryGuess.dialCode)
    ) ? countryGuess.dialCode : '';

    const formatNumber = (text: any, country: any): any => {
        if (!country) return text;

        const { format } = country;
        const { disableCountryCode, enableAreaCodeStretch, enableLongNumbers, autoFormat } = defaultProps;
        let pattern;

        if (disableCountryCode) {
            pattern = format.split(' ');
            pattern.shift();
            pattern = pattern.join(' ');
        } else {
            if (enableAreaCodeStretch && country.isAreaCode) {
                pattern = format.split(' ');
                pattern[1] = pattern[1].replace(/\.+/, ''.padEnd(country.areaCodeLength, '.'));
                pattern = pattern.join(' ');
            } else {
                pattern = format;
            }
        }

        if (!text || text.length === 0) {
            return disableCountryCode ? '' : defaultProps.prefix;
        }

        // for all strings with length less than 3, just return it (1, 2 etc.)
        // also return the same text if the selected country has no fixed format
        if ((text && text.length < 2) || !pattern || !autoFormat) {
            return disableCountryCode ? text : defaultProps.prefix+text;
        }

        const formattedObject = reduce(pattern, (acc: any, character: any): any => {
            if (acc.remainingText.length === 0) {
                return acc;
            }

            if (character !== '.') {
                return {
                    formattedText: acc.formattedText + character,
                    remainingText: acc.remainingText,
                };
            }

            const [ head, ...tail ] = acc.remainingText;

            return {
                formattedText: acc.formattedText + head,
                remainingText: tail,
            };
        }, {
            formattedText: '',
            remainingText: text.split(''),
        });

        let formattedNumber;

        if (enableLongNumbers) {
            formattedNumber = formattedObject.formattedText + formattedObject.remainingText.join('');
        } else {
            formattedNumber = formattedObject.formattedText;
        }

        // Always close brackets
        if (formattedNumber.includes('(') && !formattedNumber.includes(')')) formattedNumber += ')';
        return formattedNumber;
    };

    const formattedNumber = (inputNumber === '' && countryGuess === 0) ? '' :
        formatNumber(
            (defaultProps.disableCountryCode ? '' : dialCode) + inputNumber,
            countryGuess.name ? countryGuess : undefined,
        );

    return (
        <span className={className}>
            {formattedNumber}
        </span>
    );
}
